import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService } from 'app/common/service/api.service';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import {
    CaseDetail,
    CaseEvent,
    CaseSummary,
    ChoiceStage,
    ClientRequest,
    DecisionAnswer,
    Financing,
    Me,
    NewTicket,
    PanelDocument,
    Policy,
    RequestResult,
    RiskCategory,
    Ticket,
    TicketSummary,
    UploadStage,
    Vehicle,
} from './panel.models';

/**
 * API panelu v1 (backend panelu → CRM). Zakres klienta wyznacza backend z tokenu —
 * front nigdy nie wysyła listy klientów.
 */
@Injectable({ providedIn: 'root' })
export class PanelApiService {
    private readonly api = inject(ApiService);
    private readonly http = inject(HttpClient);

    me(): Observable<Me> {
        return this.api.get<Me>('/me');
    }

    vehicles(): Observable<Vehicle[]> {
        return this.api.get<Vehicle[]>('/vehicles');
    }

    policies(): Observable<Policy[]> {
        return this.api.get<Policy[]>('/policies');
    }

    financing(): Observable<Financing[]> {
        return this.api.get<Financing[]>('/financing');
    }

    cases(): Observable<CaseSummary[]> {
        return this.api.get<CaseSummary[]>('/cases');
    }

    caseDetail(id: number): Observable<CaseDetail> {
        return this.api.get<CaseDetail>(`/cases/${id}`);
    }

    events(limit = 20): Observable<CaseEvent[]> {
        return this.api.get<CaseEvent[]>(`/events?limit=${limit}`);
    }

    documents(): Observable<PanelDocument[]> {
        return this.api.get<PanelDocument[]>('/documents');
    }

    // ------------------------------------------------------------------ akcje klienta (Issue 23)

    /** Wybór oferty polisy, wariantu finansowania albo GAP — odpowiedź to sprawa po zmianie (stan z CRM). */
    choose(caseId: number, stageCode: ChoiceStage, optionId: string): Observable<CaseDetail> {
        return this.http.post<CaseDetail>(`${environment.apiUrl}/cases/${caseId}/choices`, { stageCode, optionId }, { headers: this.idempotency() });
    }

    /** „Żadna mi nie pasuje — chcę porozmawiać” w etapie z wyborem (Issue 43): zadanie dla opiekuna, etap bez zmian. */
    talk(caseId: number, stageCode: ChoiceStage): Observable<RequestResult> {
        return this.http.post<RequestResult>(`${environment.apiUrl}/cases/${caseId}/talk`, { stageCode }, { headers: this.idempotency() });
    }

    /** Plik od klienta do etapu (PDF, JPG, PNG do 5 MB). */
    upload(caseId: number, stageCode: UploadStage, file: File): Observable<CaseDetail> {
        const body = new FormData();

        body.append('stageCode', stageCode);
        body.append('file', file, file.name);

        return this.http.post<CaseDetail>(`${environment.apiUrl}/cases/${caseId}/documents`, body, { headers: this.idempotency() });
    }

    /** „Chcę porozmawiać” o umowie (D7), „Chcę wznowić polisę” (D8) albo „Poproś o kontakt” (Issue 38). */
    request(body: ClientRequest): Observable<RequestResult> {
        return this.http.post<RequestResult>(`${environment.apiUrl}/requests`, body, { headers: this.idempotency() });
    }

    // ------------------------------------------------------------------ zgłoszenia (Issue 40)

    tickets(): Observable<TicketSummary[]> {
        return this.api.get<TicketSummary[]>('/tickets');
    }

    ticket(id: number): Observable<Ticket> {
        return this.api.get<Ticket>(`/tickets/${id}`);
    }

    /** Nowe zgłoszenie — pliki idą potem po jednym (`ticketFile`), żeby żadne żądanie nie przekroczyło limitu. */
    createTicket(ticket: NewTicket): Observable<Ticket> {
        const body = new FormData();

        body.append('category', ticket.category);
        body.append('subject', ticket.subject);
        body.append('body', ticket.body);
        body.append('refs', JSON.stringify(ticket.refs));
        body.append('contact', ticket.contact);
        body.append('urgent', String(ticket.urgent));

        return this.http.post<Ticket>(`${environment.apiUrl}/tickets`, body, { headers: this.idempotency() });
    }

    ticketComment(id: number, text: string): Observable<Ticket> {
        return this.http.post<Ticket>(`${environment.apiUrl}/tickets/${id}/comments`, { body: text }, { headers: this.idempotency() });
    }

    ticketFile(id: number, file: File): Observable<Ticket> {
        const body = new FormData();

        body.append('file', file, file.name);

        return this.http.post<Ticket>(`${environment.apiUrl}/tickets/${id}/files`, body, { headers: this.idempotency() });
    }

    decide(id: number, decisionId: string, answer: DecisionAnswer, comment: string | null): Observable<Ticket> {
        return this.http.post<Ticket>(`${environment.apiUrl}/tickets/${id}/decisions/${decisionId}`, { answer, comment }, { headers: this.idempotency() });
    }

    /** Prośba o zdjęcia (Issue 50): zdjęcie do pozycji, usunięcie przed sprawdzeniem, „Wyślij do opiekuna”. */
    ticketPhoto(id: number, requestId: string, itemId: string, file: File): Observable<Ticket> {
        const body = new FormData();

        body.append('file', file, file.name);

        return this.http.post<Ticket>(`${environment.apiUrl}/tickets/${id}/photo-requests/${requestId}/items/${itemId}/files`, body, {
            headers: this.idempotency(),
        });
    }

    removeTicketPhoto(id: number, requestId: string, uploadId: string): Observable<Ticket> {
        return this.http.post<Ticket>(`${environment.apiUrl}/tickets/${id}/photo-requests/${requestId}/files/${uploadId}/delete`, {}, {
            headers: this.idempotency(),
        });
    }

    submitTicketPhotos(id: number, requestId: string): Observable<Ticket> {
        return this.http.post<Ticket>(`${environment.apiUrl}/tickets/${id}/photo-requests/${requestId}/submit`, {}, { headers: this.idempotency() });
    }

    reopen(id: number, reason: string | null): Observable<Ticket> {
        return this.http.post<Ticket>(`${environment.apiUrl}/tickets/${id}/reopen`, { reason }, { headers: this.idempotency() });
    }

    risks(): Observable<RiskCategory[]> {
        return this.api.get<RiskCategory[]>('/dictionaries/risks');
    }

    /** Jeden klucz na jedną akcję klienta — ponowienie w sieci nie zapisze jej drugi raz (CRM pamięta odpowiedź). */
    private idempotency(): HttpHeaders {
        const key = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k${Date.now()}${Math.random().toString(36).slice(2, 10)}`;

        return new HttpHeaders({ 'Idempotency-Key': key });
    }

    /** Pełny adres podpisanego linku do pliku (href jest względny wobec /api/v1). */
    fileUrl(href: string): string {
        return environment.apiUrl + href;
    }
}
